class PredictiveSearch extends HTMLElement {
  static cache = new Map();
  static cacheLimit = 40;
  static instanceCount = 0;

  constructor() {
    super();

    this.instanceId = `predictive-search-${++PredictiveSearch.instanceCount}`;
    this.abortController = null;
    this.requestId = 0;
    this.correctionRequestId = 0;
    this.searchTerm = '';
    this.isOpen = false;
    this.isComposing = false;
    this.isConnectedToEvents = false;

    this.onInput = this.debounce(this.handleInput.bind(this), 250);
    this.onCorrectionInput = () => {
      this.correctionRequestId += 1;
      this.resolvedCorrectionQuery = '';
      this.predictiveSearchResults?.querySelector('[data-predictive-correction-option]')?.remove();
    };
    this.onFormSubmit = this.handleFormSubmit.bind(this);
    this.onFocus = this.handleFocus.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onResultClick = this.handleResultClick.bind(this);
    this.onCompositionStart = () => {
      this.isComposing = true;
    };
    this.onCompositionEnd = (event) => {
      this.isComposing = false;
      this.handleInput(event);
    };
    this.onModalClosed = () => this.close();
  }

  connectedCallback() {
    if (this.isConnectedToEvents) return;

    this.input = this.querySelector('input[type="search"]');
    this.form = this.input?.form;
    this.searchScope = this.closest('.search-parent-js') || this;
    this.predictiveSearchResults =
      this.querySelector('[data-predictive-search]') ||
      this.searchScope.querySelector('[data-predictive-search]');
    this.statusElement =
      this.querySelector('.predictive-search-status') ||
      this.searchScope.querySelector('.predictive-search-status');

    if (!this.input || !this.form || !this.predictiveSearchResults || !this.statusElement) {
      console.error('Predictive search could not initialize because required markup is missing.');
      return;
    }

    this.ensureUniqueInputId();
    this.predictiveSearchResults
      .querySelector('.predictive-search__loading-state.hide')
      ?.classList.remove('hide');

    this.form.addEventListener('submit', this.onFormSubmit);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('input', this.onCorrectionInput);
    this.input.addEventListener('focus', this.onFocus);
    this.input.addEventListener('compositionstart', this.onCompositionStart);
    this.input.addEventListener('compositionend', this.onCompositionEnd);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeydown);
    this.searchScope.addEventListener('click', this.onResultClick);
    document.body.addEventListener('modalClosed', this.onModalClosed);
    this.isConnectedToEvents = true;
  }

  disconnectedCallback() {
    if (!this.isConnectedToEvents) return;

    this.form.removeEventListener('submit', this.onFormSubmit);
    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('input', this.onCorrectionInput);
    this.input.removeEventListener('focus', this.onFocus);
    this.input.removeEventListener('compositionstart', this.onCompositionStart);
    this.input.removeEventListener('compositionend', this.onCompositionEnd);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeydown);
    this.searchScope.removeEventListener('click', this.onResultClick);
    document.body.removeEventListener('modalClosed', this.onModalClosed);
    this.onInput.cancel();
    window.clearTimeout(this.liveRegionTimeout);
    this.abortActiveRequest();
    this.correctionRequestId += 1;
    this.isConnectedToEvents = false;
  }

  debounce(callback, wait) {
    let timeout;
    const debounced = (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
    debounced.cancel = () => window.clearTimeout(timeout);
    return debounced;
  }

  ensureUniqueInputId() {
    if (this.input.dataset.predictiveSearchOwner === this.instanceId) return;

    const currentId = this.input.id || 'Search';
    const baseId = this.input.dataset.predictiveSearchBaseId || currentId;
    const inputId = `${baseId}-${this.instanceId}`;
    const label = Array.from(this.querySelectorAll('label[for]')).find(
      (candidate) => candidate.getAttribute('for') === currentId
    );

    this.input.dataset.predictiveSearchBaseId = baseId;
    this.input.dataset.predictiveSearchOwner = this.instanceId;
    this.input.id = inputId;
    if (label) label.setAttribute('for', inputId);
  }

  getQuery() {
    return this.input.value.trim().replace(/\s+/g, ' ');
  }

  getSearchConfig(searchTerm) {
    return window.WokieeSearchQuery.parseInput(searchTerm);
  }

  getCacheKey(searchConfig) {
    return `${searchConfig.stockMode}\u0000${searchConfig.unavailableMode}\u0000${searchConfig.query.toLocaleLowerCase()}`;
  }

  isCurrentSearchConfig(searchConfig) {
    return this.currentSearchConfig?.query === searchConfig.query &&
      this.currentSearchConfig?.unavailableMode === searchConfig.unavailableMode &&
      this.currentSearchConfig?.stockMode === searchConfig.stockMode;
  }

  getFullSearchQuery(searchConfig) {
    const hasCurrentMarkup = this.isRenderedSearchConfig(searchConfig);
    if (hasCurrentMarkup && this.resolvedCorrectionQuery) return this.resolvedCorrectionQuery;
    const texts = hasCurrentMarkup ? this.getCompletionTexts() : [];
    return window.WokieeSearchQuery.fullQuery(searchConfig.query, texts);
  }

  getCompletionTexts() {
    // Labels and stock notices are interface text, never search completions.
    return Array.from(this.predictiveSearchResults.querySelectorAll('.predictive-search__item-heading'))
      .map((heading) => heading.textContent || '');
  }

  getProductCompletionTexts() {
    return Array.from(this.predictiveSearchResults.querySelectorAll('[data-predictive-product-option]'))
      .flatMap((product) => Array.from(product.querySelectorAll('.predictive-search__item-heading')))
      .map((heading) => heading.textContent || '');
  }

  isRenderedSearchConfig(searchConfig) {
    return this.renderedSearchConfig?.query === searchConfig.query &&
      this.renderedSearchConfig?.unavailableMode === searchConfig.unavailableMode &&
      this.renderedSearchConfig?.stockMode === searchConfig.stockMode;
  }

  prepareFormSubmission() {
    const searchConfig = this.getSearchConfig(this.getQuery());
    if (!searchConfig.query) return null;

    // The final URL excludes '%'. Keep the typed input unchanged while the
    // async resolver runs so repeated Enter preserves the requested stock mode.
    const unavailableInput = this.form.querySelector(
      '[data-search-unavailable-products], input[name="options[unavailable_products]"]'
    );
    if (unavailableInput) unavailableInput.value = searchConfig.unavailableMode;

    return searchConfig;
  }

  handleInput(event) {
    if (this.isComposing || event?.isComposing) return;

    const searchConfig = this.getSearchConfig(this.getQuery());
    this.searchTerm = searchConfig.query;

    if (!searchConfig.query) {
      this.clearSearch();
      return;
    }

    this.getSearchResults(searchConfig);
  }

  handleFormSubmit(event) {
    this.onInput.cancel();
    const searchConfig = this.prepareFormSubmission();
    if (!searchConfig) {
      event.preventDefault();
      return;
    }

    // Never open a stale selection left from the previous debounced query.
    const selectedAction = this.isRenderedSearchConfig(searchConfig)
      ? this.getSelectedAction()
      : null;
    if (selectedAction) {
      event.preventDefault();
      selectedAction.click();
      return;
    }

    event.preventDefault();
    window.WokieeSearchSubmit.submit(this.form, searchConfig,
      this.isRenderedSearchConfig(searchConfig) ? this.getProductCompletionTexts() : []);
  }

  handleFocus() {
    const searchConfig = this.getSearchConfig(this.getQuery());
    if (!searchConfig.query) return;

    if (!this.isCurrentSearchConfig(searchConfig)) {
      this.searchTerm = searchConfig.query;
      this.getSearchResults(searchConfig);
    } else if (this.getAttribute('results') === 'true') {
      this.open();
      this.requestCorrection(this.requestId);
    } else {
      this.getSearchResults(searchConfig);
    }
  }

  handleResultClick(event) {
    const link = event.target.closest?.('a[href]');
    if (!link || !this.predictiveSearchResults.contains(link)) return;
    window.WokieeSearchSubmit.cancel(this.form);
    if (!link.hasAttribute('data-search-for-link') || event.button > 0 ||
        event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const searchConfig = this.getSearchConfig(this.getQuery());
    if (!searchConfig.query) return;
    event.preventDefault();
    window.WokieeSearchSubmit.submit(this.form, searchConfig,
      this.isRenderedSearchConfig(searchConfig) ? this.getProductCompletionTexts() : []);
  }

  handleFocusOut() {
    window.setTimeout(() => {
      if (!this.searchScope.contains(document.activeElement)) this.close();
    });
  }

  handleKeydown(event) {
    if (this.isComposing || event.isComposing) return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption(1);
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
        break;
      case 'Enter': {
        const searchConfig = this.getSearchConfig(this.getQuery());
        const selectedAction = this.isRenderedSearchConfig(searchConfig)
          ? this.getSelectedAction()
          : null;
        if (this.isOpen && selectedAction) {
          event.preventDefault();
          selectedAction.click();
        } else if (this.getSearchConfig(this.getQuery()).query) {
          event.preventDefault();
          if (typeof this.form.requestSubmit === 'function') {
            this.form.requestSubmit();
          } else {
            this.form.querySelector('[type="submit"]')?.click();
          }
        }
        break;
      }
      default:
        break;
    }
  }

  getVisibleOptions() {
    return Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]')).filter(
      (option) => option.offsetParent !== null && option.querySelector('a, button')
    );
  }

  getSelectedOption() {
    return this.predictiveSearchResults.querySelector('[role="option"][aria-selected="true"]');
  }

  getSelectedAction() {
    return this.getSelectedOption()?.querySelector('a, button') || null;
  }

  switchOption(step) {
    if (!this.isOpen || !this.isRenderedSearchConfig(this.getSearchConfig(this.getQuery()))) return;

    const options = this.getVisibleOptions();
    if (!options.length) return;

    const selectedOption = this.getSelectedOption();
    const selectedIndex = options.indexOf(selectedOption);
    const nextIndex = selectedIndex === -1
      ? (step > 0 ? 0 : options.length - 1)
      : (selectedIndex + step + options.length) % options.length;
    const nextOption = options[nextIndex];

    if (selectedOption) selectedOption.setAttribute('aria-selected', 'false');
    nextOption.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', nextOption.id);
    this.statusElement.textContent = '';
    nextOption.scrollIntoView?.({ block: 'nearest' });
  }

  abortActiveRequest() {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
  }

  getSearchResults(searchConfig) {
    // A new request must not leave a keyboard-selected product from older
    // markup active while currentSearchConfig already describes the new term.
    if (!this.isCurrentSearchConfig(searchConfig)) {
      this.close();
      this.removeAttribute('results');
    }
    this.currentSearchConfig = searchConfig;
    const cacheKey = this.getCacheKey(searchConfig);
    const currentRequestId = ++this.requestId;

    this.setLiveRegionLoadingState();
    this.abortActiveRequest();

    const suffix = window.WokieeSearchQuery.skuSuffix(searchConfig.query);
    if (suffix) {
      const url = new URL(window.routes?.search_url || '/search', window.location.origin);
      url.searchParams.set('q', searchConfig.query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      window.WokieeSearchQuery.applyStockParams(url.searchParams, searchConfig);
      const href = url.toString().replaceAll('&', '&amp;').replaceAll('"', '&quot;');
      // The only interpolated label data is an exactly validated pair of digits.
      // Native predictive search cannot return exact SKU-suffix matches.
      this.renderSearchResults(`
        <div id="predictive-search-results" class="predictive-search-results" role="listbox">
          <div id="predictive-search-option-search-keywords" class="predictive-search-option-search-keywords" role="option" aria-selected="false">
            <a id="search-for-button" class="link link-hover-span body2" data-search-for-link tabindex="-1"
               href="${href}" aria-label="Pretraži SKU sa završetkom -${suffix}">
              <span>Pretraži SKU sa završetkom -${suffix}</span>
            </a>
          </div>
        </div>`, currentRequestId);
      return;
    }

    const cachedMarkup = PredictiveSearch.cache.get(cacheKey);
    if (cachedMarkup) {
      this.renderSearchResults(cachedMarkup, currentRequestId);
      return;
    }

    const params = new URLSearchParams();
    params.set('q', searchConfig.query);
    params.set('resources[type]', 'product,query');
    params.set('resources[limit]', searchConfig.stockMode === 'soldout' ? '10' : '5');
    params.set('resources[limit_scope]', 'each');
    params.set(
      'resources[options][fields]',
      'title,body,product_type,vendor,variants.title,variants.sku,variants.barcode,tag'
    );
    params.set('resources[options][prefix]', 'last');
    params.set('resources[options][unavailable_products]', searchConfig.unavailableMode);
    params.set('section_id', 'predictive-search');

    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const predictiveSearchUrl = window.routes?.predictive_search_url || '/search/suggest';

    fetch(`${predictiveSearchUrl}?${params.toString()}`, {
      signal,
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (currentRequestId !== this.requestId) return;

        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const section = responseDocument.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search response did not contain the expected section.');

        this.setCachedResult(cacheKey, section.innerHTML);
        this.renderSearchResults(section.innerHTML, currentRequestId);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || currentRequestId !== this.requestId) return;

        console.error(error);
        this.removeAttribute('results');
        this.close();
      })
      .finally(() => {
        if (currentRequestId === this.requestId) this.abortController = null;
      });
  }

  setCachedResult(cacheKey, markup) {
    if (PredictiveSearch.cache.has(cacheKey)) PredictiveSearch.cache.delete(cacheKey);
    PredictiveSearch.cache.set(cacheKey, markup);

    if (PredictiveSearch.cache.size > PredictiveSearch.cacheLimit) {
      PredictiveSearch.cache.delete(PredictiveSearch.cache.keys().next().value);
    }
  }

  renderSearchResults(resultsMarkup, requestId) {
    if (requestId !== this.requestId) return;

    this.correctionRequestId += 1;
    this.resolvedCorrectionQuery = '';
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.renderedSearchConfig = { ...this.currentSearchConfig };
    this.filterStockResults();
    this.namespaceResultIds();
    this.updateSearchForLink();
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
    this.requestCorrection(requestId);
  }

  async requestCorrection(requestId) {
    const helper = window.WokieeSearchQuery;
    const searchConfig = this.currentSearchConfig;
    if (typeof helper.resolveSearch !== 'function' || !searchConfig?.query ||
        !this.isOpen || !this.isConnected || !this.isRenderedSearchConfig(searchConfig) ||
        !helper.isPlainQuery(searchConfig.query) ||
        !/^[\p{L}\p{M}]+(?:\s+[\p{L}\p{M}]+)*$/u.test(searchConfig.query) ||
        !searchConfig.query.split(/\s+/).some((word) => Array.from(word).length >= 5) ||
        this.predictiveSearchResults.querySelector('[data-predictive-product-option]') ||
        this.predictiveSearchResults.querySelector('[data-predictive-correction-option]')) return;

    const correctionRequestId = ++this.correctionRequestId;
    const inputValue = this.input.value;
    try {
      // Enter shares this resolver promise. Closing the popup only cancels this
      // consumer; it must not abort a search submission waiting on the same work.
      const resolved = await helper.resolveSearch(searchConfig.query, searchConfig.unavailableMode);
      if (correctionRequestId !== this.correctionRequestId || requestId !== this.requestId ||
          !this.isOpen || !this.isConnected || this.input.value !== inputValue ||
          !this.isCurrentSearchConfig(searchConfig) || !this.isRenderedSearchConfig(searchConfig) ||
          !resolved?.query || !resolved.correction) return;

      const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
      if (!listbox) return;
      const url = new URL(window.routes?.search_url || '/search', window.location.origin);
      url.searchParams.set('q', resolved.query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      helper.applyStockParams(url.searchParams, searchConfig);

      const option = document.createElement('div');
      option.id = `${this.instanceId}-correction-${correctionRequestId}`;
      option.className = 'predictive-search-option-search-keywords';
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('data-predictive-correction-option', '');
      const link = document.createElement('a');
      link.className = 'link link-hover-span body2';
      link.href = url.toString();
      link.tabIndex = -1;
      link.textContent = `Pretraži i: ${resolved.correction}`;
      option.appendChild(link);
      const fullSearchLink = listbox.querySelector('[data-search-for-link]');
      const fullSearchOption = fullSearchLink?.closest('[role="option"]');
      listbox.insertBefore(option, fullSearchOption?.parentNode === listbox ? fullSearchOption : null);
      this.resolvedCorrectionQuery = resolved.query;
      if (fullSearchLink) fullSearchLink.href = url.toString();
      // Native predictive results can be empty while the verified lexical
      // alternative has matches on the full results page.
      this.setLiveRegionText(`${link.textContent}. Pritisni Enter za sve rezultate.`);
    } catch (_) {
      // A correction is optional; the original search and links remain usable.
    }
  }

  filterStockResults() {
    this.stockResultsStatus = '';
    const suffix = window.WokieeSearchQuery.skuSuffix(this.currentSearchConfig?.query);
    if (suffix) {
      this.stockResultsStatus = `Pritisni Enter za pretragu SKU-a sa završetkom -${suffix}.`;
      return;
    }
    if (this.currentSearchConfig?.stockMode !== 'soldout') return;

    // Predictive search has no unavailable-only endpoint. Filter its limited
    // seed list using the same variant availability meaning as Shopify facets.
    let productCount = 0;
    this.predictiveSearchResults.querySelectorAll('[data-predictive-product-option]').forEach((option) => {
      const hasUnavailableVariant = option.dataset.variantUnavailable === 'true' ||
        option.dataset.productAvailable === 'false';
      if (!hasUnavailableVariant || productCount >= 5) {
        option.remove();
        return;
      }
      productCount += 1;
      const link = option.querySelector('a[href]');
      if (link && option.dataset.unavailableVariantId) {
        const productUrl = new URL(link.href, window.location.origin);
        productUrl.searchParams.set('variant', option.dataset.unavailableVariantId);
        link.href = productUrl.toString();
      }
    });

    let queryCount = 0;
    this.predictiveSearchResults.querySelectorAll('[data-predictive-query-option]').forEach((option) => {
      if (queryCount >= 5) option.remove();
      else queryCount += 1;
    });

    const productGroup = this.predictiveSearchResults.querySelector('[data-predictive-products-group]');
    if (productGroup) productGroup.hidden = productCount === 0;
    this.predictiveSearchResults.querySelector('.predictive-search__results-groups-wrapper')
      ?.classList.toggle('predictive-search__results-groups-wrapper--no-products', productCount === 0);

    const labels = this.predictiveSearchResults.querySelector('[data-predictive-stock-labels]')?.dataset || {};
    const productHeading = this.predictiveSearchResults.querySelector('[data-predictive-products-heading]');
    if (productHeading) productHeading.textContent = `${labels.soldoutHeading || 'Out of stock'} (${productCount})`;
    const notice = this.predictiveSearchResults.querySelector('[data-predictive-stock-note]');
    const message = productCount
      ? labels.soldoutMore || 'Press Enter to see all out-of-stock results.'
      : labels.soldoutEmpty || 'No out-of-stock suggestions in this short list. Press Enter for all results.';
    if (notice) {
      notice.textContent = message;
      notice.hidden = false;
    }
    const countTemplate = labels.soldoutCount || 'Product suggestions: __products__. Search suggestions: __queries__.';
    this.stockResultsStatus = `${countTemplate.replace('__products__', productCount).replace('__queries__', queryCount)} ${message}`;
  }

  namespaceResultIds() {
    const idMap = new Map();

    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element, index) => {
      const originalId = element.id;
      const uniqueId = `${this.instanceId}-${originalId}-${index}`;
      if (!idMap.has(originalId)) idMap.set(originalId, uniqueId);
      element.id = uniqueId;
    });

    this.predictiveSearchResults
      .querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [aria-owns]')
      .forEach((element) => {
        ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach((attribute) => {
          if (!element.hasAttribute(attribute)) return;
          const ids = element
            .getAttribute(attribute)
            .split(/\s+/)
            .map((id) => idMap.get(id) || id)
            .join(' ');
          element.setAttribute(attribute, ids);
        });
      });

    const resultsListbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (resultsListbox) {
      this.input.setAttribute('aria-controls', resultsListbox.id);
      this.input.setAttribute('aria-owns', resultsListbox.id);
    }
    this.input.removeAttribute('aria-activedescendant');
  }

  updateSearchForLink() {
    if (!this.currentSearchConfig?.query) return;

    const searchUrl = window.routes?.search_url || '/search';
    const searchRoute = new URL(searchUrl, window.location.origin);
    const fullSearchQuery = this.getFullSearchQuery(this.currentSearchConfig);

    this.predictiveSearchResults.querySelectorAll('a[href]').forEach((link) => {
      const linkUrl = new URL(link.getAttribute('href'), window.location.origin);
      if (linkUrl.origin !== searchRoute.origin || linkUrl.pathname !== searchRoute.pathname) return;

      if (link.hasAttribute('data-search-for-link')) {
        linkUrl.searchParams.set('q', fullSearchQuery);
      }
      if (!linkUrl.searchParams.has('q')) return;

      linkUrl.searchParams.set('q', window.WokieeSearchQuery.fullQuery(
        linkUrl.searchParams.get('q'), this.getCompletionTexts()
      ));
      linkUrl.searchParams.set('type', 'product');
      linkUrl.searchParams.set('options[prefix]', 'none');
      window.WokieeSearchQuery.applyStockParams(linkUrl.searchParams, this.currentSearchConfig);
      link.href = linkUrl.toString();
    });
  }

  setLiveRegionLoadingState() {
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || '';
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
    if (this.getAttribute('results') === 'true') this.open();
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    window.clearTimeout(this.liveRegionTimeout);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    this.liveRegionTimeout = window.setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    if (this.stockResultsStatus) {
      this.setLiveRegionText(this.stockResultsStatus);
      return;
    }
    const countValue = this.predictiveSearchResults.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );
    this.setLiveRegionText(countValue?.textContent?.trim() || '');
  }

  getResultsMaxHeight() {
    return Math.max(200, window.innerHeight - this.getBoundingClientRect().bottom - 24);
  }

  open() {
    if (!this.closest('.search-parent-js')) {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close() {
    this.correctionRequestId += 1;
    const selectedOption = this.getSelectedOption();
    if (selectedOption) selectedOption.setAttribute('aria-selected', 'false');

    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }

  clearSearch() {
    this.searchTerm = '';
    this.currentSearchConfig = null;
    this.renderedSearchConfig = null;
    this.requestId += 1;
    this.abortActiveRequest();
    this.removeAttribute('results');
    this.close();
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
